import { getAppEnv } from '@/lib/env/app-env';

/**
 * Shared tracesSampler for all three Sentry runtimes (client, server, edge) —
 * one implementation so the rules can't drift, same reason before-send.ts is
 * shared.
 *
 * Why this exists: a flat tracesSampleRate of 0.1 was burning the span quota
 * on traffic nobody ever looks at (July 2026, 61% of the 5M plan by day 20):
 *
 *   - `GET /login` alone was ~25% of ALL stored spans. It's not customers —
 *     bot scanners plus every logged-out request to a protected path gets
 *     middleware-redirected there, and each hit fans out into a dozen+ spans.
 *   - The /monitoring tunnel, /api/csp-report and /sw.js are pure plumbing;
 *     their traces carry no signal at all.
 *   - The every-5-minute crons trace themselves ~9k times a month each.
 *   - dev/preview/demo traced at the same 10% as prod (~13% of the quota).
 *
 * Transaction names differ per runtime — server: "GET /login", client
 * pageload/navigation: "/login", middleware: "middleware GET" — so rules
 * match on substrings, not exact names.
 */

type TracesSamplerContext = {
  name: string;
  parentSampled?: boolean;
  inheritOrSampleWith: (fallbackRate: number) => number;
};

/** Prod sample rate for ordinary traffic — the pre-existing flat rate. */
const BASE_RATE = 0.1;
/** Trickle rate for high-volume/low-signal routes and non-prod envs. */
const TRICKLE_RATE = 0.01;

export function muckaTracesSampler(ctx: TracesSamplerContext): number {
  const { name } = ctx;

  // Zero-signal plumbing: the Sentry tunnel route (tracing it means Sentry
  // traffic generates more Sentry traffic), CSP violation reports, and the
  // service worker fetch. Hard 0 — ignore any parent sampling decision.
  if (
    name.includes('/monitoring') ||
    name.includes('/api/csp-report') ||
    name.includes('/sw.js')
  ) {
    return 0;
  }

  // Bot-dominated: /login is the middleware redirect target for every
  // logged-out request, so scanners land here in bulk. Keep a trickle so
  // genuine login-page perf regressions still surface.
  if (name.includes('/login')) return TRICKLE_RATE;

  // Middleware root transactions ("middleware GET" / "middleware POST") run
  // on every single request and are rarely the interesting part of a trace.
  // Child middleware spans inside a sampled page trace are unaffected.
  if (name.startsWith('middleware')) return TRICKLE_RATE;

  // Crons fire every 5 minutes all day; a 1% sample still gives ~85
  // traces/month per cron for latency spot-checks.
  if (name.includes('/api/cron/')) return TRICKLE_RATE;

  // Non-prod (dev / preview / demo) keeps a trickle for smoke-testing
  // instrumentation, not full prod-grade sampling.
  const base = getAppEnv() === 'prod' ? BASE_RATE : TRICKLE_RATE;

  // Respect an upstream sampling decision (distributed tracing) so traces
  // don't arrive half-sampled; fall back to our rate at the trace root.
  return ctx.inheritOrSampleWith(base);
}
